import { colors } from "themes/variables"
import { themes } from "constants/theme"
import { BOOKING_PAYMENT_STATES, FAILED_PAYMENT_STATUSES, PAID_PAYMENT_STATUSES } from "./constants"

/** The Legend's swatch colours, keyed by state, so the tile bar and the Legend cannot drift. */
const PAYMENT_STATE_COLORS = BOOKING_PAYMENT_STATES.reduce((acc, state) => ({ ...acc, [state.key]: state.color }), {})

const componentToHex = (value) => value.toString(16).padStart(2, "0")

export const rgbToHex = (r, g, b) => `#${componentToHex(r)}${componentToHex(g)}${componentToHex(b)}`

export const hexToRgb = (hex) => {
  const clean = hex.replace("#", "")
  const full = clean.length === 3 ? clean.split("").map((c) => c + c).join("") : clean
  const num = Number.parseInt(full, 16)
  return [num >> 16, (num >> 8) & 255, num & 255]
}

export const normalizeCssColorToHex = (value) => {
  if (!value) return colors.blueSurface
  if (value.startsWith("#")) return value
  const match = value.match(/rgba?\(([^)]+)\)/i)
  if (!match) return colors.blueSurface
  const [r = 255, g = 255, b = 255, a = 1] = match[1].split(",").map((p) => Number.parseFloat(p.trim()))
  return rgbToHex(
    Math.round(a * r + (1 - a) * 255),
    Math.round(a * g + (1 - a) * 255),
    Math.round(a * b + (1 - a) * 255)
  )
}

export const getDefaultCalendarColor = () => {
  if (typeof document === "undefined" || !document.body) return colors.blueSurface
  const primaryRgb = getComputedStyle(document.body).getPropertyValue("--color-primary-rgb").trim()
  if (!primaryRgb) return colors.blueSurface
  const parts = primaryRgb.split(",").map((n) => Number(n.trim()))
  if (parts.length < 3 || parts.some(Number.isNaN)) return colors.blueSurface
  const [r, g, b] = parts
  const alpha = 0.05
  return rgbToHex(
    Math.round(alpha * r + (1 - alpha) * 255),
    Math.round(alpha * g + (1 - alpha) * 255),
    Math.round(alpha * b + (1 - alpha) * 255)
  )
}

const relativeLuminance = (hex) => {
  const [r, g, b] = hexToRgb(hex)
  return [r, g, b].reduce((acc, v, i) => {
    const s = v / 255
    const c = s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
    return acc + c * [0.2126, 0.7152, 0.0722][i]
  }, 0)
}

const getContrastRatio = (a, b) => {
  const la = relativeLuminance(a)
  const lb = relativeLuminance(b)
  const lighter = Math.max(la, lb)
  const darker = Math.min(la, lb)
  return (lighter + 0.05) / (darker + 0.05)
}

const blendHexWithWhite = (hex, alpha = 0.1) => {
  const [r, g, b] = hexToRgb(hex)
  return rgbToHex(
    Math.round(alpha * r + (1 - alpha) * 255),
    Math.round(alpha * g + (1 - alpha) * 255),
    Math.round(alpha * b + (1 - alpha) * 255)
  )
}

export const getTagBackgroundColor = (hex) => {
  const [r, g, b] = hexToRgb(hex)
  return `rgba(${r}, ${g}, ${b}, 0.1)`
}

export const getPrimaryCalendarTextColor = (theme) =>
  theme === themes.sportswoman ? colors.sportswomanBrand : colors.sportsmanBrand

export const isSameHexColor = (a, b) => a?.toLowerCase() === b?.toLowerCase()

/**
 * Resolves preview colors for a booking given its raw color or payment status token.
 * Centralises the getEventColor → isCustomColor → getEventPreviewColors branch
 * that was duplicated across every view component.
 */
export const resolveBookingColors = (colorOrStatus, defaultColor, primaryTextColor, getEventColor) => {
  const resolvedColor = getEventColor(colorOrStatus)
  const isCustomColor = resolvedColor?.startsWith("#")
  return isCustomColor
    ? getEventPreviewColors(resolvedColor, defaultColor, primaryTextColor)
    : { backgroundColor: resolvedColor, borderColor: primaryTextColor, textColor: primaryTextColor }
}

/**
 * Shared color logic used by both TilePreview (settings) and EventBlock (calendar).
 * Returns { backgroundColor, borderColor, textColor } for a given hex color.
 */
export const getEventPreviewColors = (color, defaultColor, primaryTextColor) => {
  if (isSameHexColor(color, defaultColor)) {
    // For default/reset colors, use solid background (no opacity)
    const [r, g, b] = hexToRgb(defaultColor)
    return {
      backgroundColor: `rgba(${r}, ${g}, ${b}, 1)`,
      borderColor: primaryTextColor,
      textColor: primaryTextColor
    }
  }

  const blendedBg = blendHexWithWhite(color)
  const contrast = getContrastRatio(color, blendedBg)
  const textColor = contrast >= 3 ? color : colors.textPrimary
  const borderColor = getContrastRatio(color, blendedBg) >= 1.2 ? color : colors.lines

  return {
    backgroundColor: getTagBackgroundColor(color), // Custom colors keep 0.1 opacity
    borderColor,
    textColor
  }
}

/**
 * The colour of the bar down the left of a month booking tile: the booking's payment state,
 * as the Legend spells it out. The tile's *background* still carries the experience colour,
 * so the two together say "which experience" and "is it paid".
 *
 * Returns null when the booking carries no payment status at all, which lets the caller keep
 * the experience colour rather than claim a balance is due.
 */
export const getPaymentAccentColor = (paymentStatus) => {
  if (!paymentStatus) return null
  const status = String(paymentStatus).toLowerCase()
  if (FAILED_PAYMENT_STATUSES.includes(status)) return PAYMENT_STATE_COLORS.failed
  if (PAID_PAYMENT_STATUSES.includes(status)) return PAYMENT_STATE_COLORS.paid
  return PAYMENT_STATE_COLORS.balance_due
}
